import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";

import { formatCsvDate } from "./csvReviewFormat";
import {
  ERRORED_CSV_ROWS_LIMIT,
  fetchErroredCsvRows,
  retryErroredCsvRows
} from "../../../helpers/smartUpload";
import { SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY } from "../../../helpers/components/tasks";

const ROW_REASON_COPY = {
  invalid_amount: "This row's amount couldn't be read.",
  invalid_date: "This row's date couldn't be read.",
  invalid_shipment: "This shipment couldn't be calculated."
};

const rowReason = ({ errorMessage, reason }) =>
  errorMessage || ROW_REASON_COPY[reason] || "We couldn't read this row.";

const cellText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return typeof value === "object" ? formatCsvDate(value) : String(value);
};

const inputText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return typeof value === "object" ? formatCsvDate(value) : String(value);
};

const editsPayload = (edits) =>
  Object.entries(edits)
    .filter(
      ([, { fields = {}, ignoreInReprocess }]) =>
        ignoreInReprocess || Object.keys(fields).length
    )
    .map(([rowId, { fields = {}, ignoreInReprocess }]) =>
      ignoreInReprocess ? { rowId, ignoreInReprocess: true } : { rowId, fields }
    );

const ErroredRow = ({ row, columns, edit, onEditCell, onToggleIgnore }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { fields = {}, ignoreInReprocess = false } = edit || {};

  return (
    <TableRow>
      {columns.map(({ value: field, label }) => (
        <TableCell key={field}>
          {isEditing && field in row ? (
            <TextField
              size="small"
              value={fields[field] ?? inputText(row[field])}
              inputProps={{ "aria-label": `${label} for row ${row.id}` }}
              onChange={({ target }) => onEditCell(field, target.value)}
            />
          ) : (
            cellText(fields[field] ?? row[field])
          )}
        </TableCell>
      ))}
      <TableCell>
        <Typography variant="caption" color="error">
          {rowReason(row)}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button size="small" onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? "Done" : "Edit"}
          </Button>
          <Button size="small" onClick={onToggleIgnore}>
            {ignoreInReprocess ? "Include" : "Don't retry"}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

// The rows a CSV draft couldn't read (bad amount, unparseable date...). They
// have no review line, so the fix is to correct a cell and retry the row,
// through csv-retry-errored-rows -- these rows are server-write-only.
const CsvErroredRows = ({
  companyId,
  taskDocId,
  documentFamily,
  erroredRowCount,
  refreshKey,
  isRunActive,
  onRetried
}) => {
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [isRetrying, setIsRetrying] = useState(false);
  const [message, setMessage] = useState(null);

  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (!erroredRowCount) {
      return;
    }
    fetchErroredCsvRows({ companyId, taskDocId })
      .then((erroredRows) => {
        setLoadError(null);
        return setRows(erroredRows);
      })
      .catch(() =>
        setLoadError("We couldn't load the rows that couldn't be read.")
      );
  }, [companyId, taskDocId, erroredRowCount, refreshKey, loadAttempt]);

  const columns =
    SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY.get(documentFamily) || [];

  const editRow = (rowId, change) =>
    setEdits((current) => ({
      ...current,
      [rowId]: { ...(current[rowId] || {}), ...change(current[rowId] || {}) }
    }));

  const retry = async () => {
    setIsRetrying(true);
    const response = await retryErroredCsvRows({
      companyId,
      taskDocId,
      edits: editsPayload(edits)
    });
    setIsRetrying(false);
    if (!response || response.error) {
      return setMessage({
        severity: "error",
        text:
          response?.message ||
          "We couldn't retry these rows — please try again."
      });
    }
    setEdits({});
    setMessage({
      severity: "success",
      text: `Retrying ${response.requeuedCount} ${
        response.requeuedCount === 1 ? "row" : "rows"
      } — they'll show up for review once they're read.`
    });
    return onRetried();
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6">Couldn&apos;t be read</Typography>
      <Typography variant="body2" color="textSecondary">
        {erroredRowCount} {erroredRowCount === 1 ? "row" : "rows"} in this file
        couldn&apos;t be read. Fix a cell if you need to, then retry.
        {erroredRowCount > ERRORED_CSV_ROWS_LIMIT &&
          ` Showing the first ${ERRORED_CSV_ROWS_LIMIT}.`}
      </Typography>
      {loadError && (
        <Alert
          severity="error"
          sx={{ my: 1 }}
          action={
            <Button
              size="small"
              onClick={() => setLoadAttempt((current) => current + 1)}
            >
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {message && (
        <Alert severity={message.severity} sx={{ my: 1 }}>
          {message.text}
        </Alert>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(({ value, label }) => (
                <TableCell key={value}>{label}</TableCell>
              ))}
              <TableCell>Why</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <ErroredRow
                key={row.id}
                row={row}
                columns={columns}
                edit={edits[row.id]}
                onEditCell={(field, value) =>
                  editRow(row.id, ({ fields = {} }) => ({
                    fields: { ...fields, [field]: value }
                  }))
                }
                onToggleIgnore={() =>
                  editRow(row.id, ({ ignoreInReprocess }) => ({
                    ignoreInReprocess: !ignoreInReprocess
                  }))
                }
              />
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          disabled={isRetrying || isRunActive}
          onClick={retry}
        >
          Retry rows that couldn&apos;t be read
        </Button>
        {isRunActive && (
          <Typography variant="caption" color="textSecondary">
            {" "}
            You can retry once the confirmation in progress finishes.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CsvErroredRows;
